import { SelectOption } from '@factorialco/deprecated-design-system'

import { logicOperators } from 'modules/core/components/MultiFilters/hooks/utils/logicOperators'
import type { FilterOption } from 'modules/core/components/MultiFilters/types'
import createComputed from 'modules/core/hooks/createComputed'
import i18n from 'modules/core/lib/i18n'

interface LogicOption {
  label: string
  value: string
}

export const CONDITIONS_VALUES = {
  isEmpty: 'is_empty',
  isNotEmpty: 'is_not_empty',
  isEqual: 'is_equal',
  isNotEqual: 'is_not_equal',
  contains: 'contains',
  notContains: 'not_contains',
  after: 'after',
  before: 'before',
  equalAfter: 'equal_after',
  equalBefore: 'equal_before',
}

const useFilterItem = () => {
  const useSelectedField = createComputed((fields: readonly FilterOption[], field: string) => {
    return fields.find((fieldOption: FilterOption) => fieldOption.value === field)
  })

  const useNeedValue = createComputed((condition: string) => {
    return ![CONDITIONS_VALUES.isEmpty, CONDITIONS_VALUES.isNotEmpty, ''].includes(condition)
  })

  const useLogicOperatorLabel = createComputed(
    (logicOperator: string, logicOptions: LogicOption[]) => {
      const logicOption = logicOptions.find(
        (logicOption: LogicOption) => logicOption.value === logicOperator
      )

      return logicOption?.label || logicOptions[0]?.label
    }
  )

  const useConditions = createComputed(
    (selectedField: FilterOption | undefined, isCustomAction: boolean): SelectOption[] => {
      const conditions: Record<string, SelectOption[]> = {
        string: [
          {
            label: i18n.t('filters.multi_filters.conditions.is_empty'),
            value: CONDITIONS_VALUES.isEmpty,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.is_not_empty'),
            value: CONDITIONS_VALUES.isNotEmpty,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.is_equal'),
            value: CONDITIONS_VALUES.isEqual,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.is_not_equal'),
            value: CONDITIONS_VALUES.isNotEqual,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.contains'),
            value: CONDITIONS_VALUES.contains,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.not_contains'),
            value: CONDITIONS_VALUES.notContains,
          },
        ],
        date: [
          {
            label: i18n.t('filters.multi_filters.conditions.is_empty'),
            value: CONDITIONS_VALUES.isEmpty,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.is_not_empty'),
            value: CONDITIONS_VALUES.isNotEmpty,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.after'),
            value: CONDITIONS_VALUES.after,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.before'),
            value: CONDITIONS_VALUES.before,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.equal_after'),
            value: CONDITIONS_VALUES.equalAfter,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.equal_before'),
            value: CONDITIONS_VALUES.equalBefore,
          },
        ],
        single_choice: [
          {
            label: i18n.t('filters.multi_filters.conditions.is_empty'),
            value: CONDITIONS_VALUES.isEmpty,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.is_not_empty'),
            value: CONDITIONS_VALUES.isNotEmpty,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.is_equal'),
            value: CONDITIONS_VALUES.isEqual,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.is_not_equal'),
            value: CONDITIONS_VALUES.isNotEqual,
          },
        ],
        equal_choice: [
          {
            label: i18n.t('filters.multi_filters.conditions.is_equal'),
            value: CONDITIONS_VALUES.isEqual,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.is_not_equal'),
            value: CONDITIONS_VALUES.isNotEqual,
          },
        ],
        custom: [
          {
            label: i18n.t('filters.multi_filters.conditions.is_equal'),
            value: CONDITIONS_VALUES.isEqual,
          },
          {
            label: i18n.t('filters.multi_filters.conditions.is_not_equal'),
            value: CONDITIONS_VALUES.isNotEqual,
          },
        ],
      }

      const customActionConditions: SelectOption[] = [
        {
          label: i18n.t('filters.multi_filters.conditions.is_equal'),
          value: 'is_equal',
        },
      ]

      return isCustomAction
        ? customActionConditions
        : conditions[selectedField?.type || 'string'] ?? []
    }
  )

  const logicOptions = [
    {
      label: i18n.t('filters.multi_filters.logic_operators.and'),
      value: logicOperators.and,
    },
    {
      label: i18n.t('filters.multi_filters.logic_operators.or'),
      value: logicOperators.or,
    },
  ]

  return {
    useSelectedField,
    useNeedValue,
    useLogicOperatorLabel,
    useConditions,
    logicOptions,
  }
}

export default useFilterItem
